import { createSlice } from "@reduxjs/toolkit";
import { fetchGlobalSearchAsync, fetchSearchFiltersAsync } from "../thunks/GlobalSearchThunk";
import { STATUS } from "./OrganizationSlice";

export interface SearchUser {
  id: string;
  first: string;
  last: string;
  email: string;
  role: string;
  mobilePhone: string | null;
  goal: string | null;
  emailVerified: boolean;
  onboardingSate: string | null;
  externalUniqueId: string;
  createdAt: string;
  organization: { id: number; name: string };
  isActive: boolean;
  invitationSentAt: string | null;
  deactivatedAt: string | null;
}

export interface SearchOrganization {
  id: number;
  name: string;
  displayName: string;
  contactName: string;
  contactEmail: string;
  parentOrgId: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  activeUsers: number;
  activeSubOrgs: number;
  totalActiveOrganizations: number | null;
  totalActiveUsers: number | null;
  subOrgs: { orgId: string; name: string; displayName: string }[];
}

export interface RoleOption {
  role: string;
  displayName: string;
}

export interface StatusOption {
  status: string;
  displayName: string;
}

export interface OrgFilterOption {
  id: string;
  name: string;
  displayName: string;
  subOrgs: { orgId: string; name: string; displayName: string }[];
}

interface GlobalSearchState {
  users: SearchUser[];
  organizations: SearchOrganization[];
  status: string;
  error: string | null;
  page: number;
  per_page: number;
  total_pages: number;
  total_items: number;
  // Filter options
  roleOptions: RoleOption[];
  statusOptions: StatusOption[];
  orgFilterOptions: OrgFilterOption[];
  filtersStatus: string;
}

const initialState: GlobalSearchState = {
  users: [],
  organizations: [],
  status: STATUS.IDLE,
  error: null,
  page: 1,
  per_page: 10,
  total_pages: 0,
  total_items: 0,
  roleOptions: [],
  statusOptions: [],
  orgFilterOptions: [],
  filtersStatus: STATUS.IDLE,
};

const globalSearchSlice = createSlice({
  name: "globalSearch",
  initialState,
  reducers: {
    resetSearchResults: (state) => {
      state.users = [];
      state.organizations = [];
      state.status = STATUS.IDLE;
      state.error = null;
      state.page = 1;
      state.total_pages = 0;
      state.total_items = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGlobalSearchAsync.pending, (state) => {
        state.status = STATUS.LOADING;
      })
      .addCase(fetchGlobalSearchAsync.fulfilled, (state, action) => {
        state.status = STATUS.SUCCESS;
        state.users = action.payload.users;
        state.organizations = action.payload.organizations;
        state.page = action.payload.page;
        state.per_page = action.payload.per_page;
        state.total_pages = action.payload.total_pages;
        state.total_items = action.payload.total_items;
      })
      .addCase(fetchGlobalSearchAsync.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.payload as string;
      })
      .addCase(fetchSearchFiltersAsync.pending, (state) => {
        state.filtersStatus = STATUS.LOADING;
      })
      .addCase(fetchSearchFiltersAsync.fulfilled, (state, action) => {
        state.filtersStatus = STATUS.SUCCESS;
        state.roleOptions = action.payload.roles;
        state.statusOptions = action.payload.statuses;
        state.orgFilterOptions = action.payload.organizations;
      })
      .addCase(fetchSearchFiltersAsync.rejected, (state, action) => {
        state.filtersStatus = STATUS.FAILED;
        state.error = action.payload as string;
      });
  },
});

export const { resetSearchResults } = globalSearchSlice.actions;
export default globalSearchSlice.reducer;
